import { Activity } from './activity';
import { User } from './user';

export type ThreadUserArgs = {
  userId?: string;
  threadId: string;
  organizationId: string;
};

export type ThreadTagsArgs = {
  tagId: string;
  threadId: string;
  role?: string;
  reason?: string;
  taggerId?: string;
};

export type ThreadUpdateStatusArgs = {
  threadId: string;
  action: ActionsBack;
};

export type ThreadMarkAsReadArgs = {
  threadId: string;
  lastReadAt: string;
  messageId: string;
};

export enum GroupResponseMode {
  ONLY_WHEN_MENTIONED = 'ONLY_WHEN_MENTIONED',
  WHEN_KNOWS = 'WHEN_KNOWS',
  ALWAYS = 'ALWAYS',
}

export enum ThreadAction {
  DELETE = 'DELETE',
  BLOCK = 'BLOCKED',
  //UNBLOCK = 'UNBLOCK',
  ARCHIVE = 'ARCHIVED',
  //UNARCHIVE = 'UNARCHIVE',
  IGNORED = 'IGNORED',
  UNIGNORE = 'ONGOING ',
  HUMAN_TAKEOVER = 'HUMAN_TAKEOVER',
  ASSISTANT_PAUSED = 'ASSISTANT_PAUSED',
  ONGOING = 'ONGOING',
}

export enum ActionsBack {
  DELETE = 'DELETE',
  BLOCK = 'BLOCK',
  ARCHIVE = 'ARCHIVE',
  SEND_IGNORE = 'IGNORE',
  SEND_UNIGNORE = 'UNIGNORE',
  HUMAN_TAKEOVER = 'HUMAN_TAKEOVER',
  PAUSE_ASSISTANT = 'PAUSE_ASSISTANT',
  RESUME_ASSISTANT = 'RESUME_ASSISTANT',
}

export const actionButtonLabels = {
  [ThreadAction.DELETE]: 'Excluir',
  [ThreadAction.BLOCK]: 'Bloquear',
  [ThreadAction.ARCHIVE]: 'Arquivar',
  [ThreadAction.UNIGNORE]: 'Não Ignorar',
  [ThreadAction.IGNORED]: 'Ignorar',
};

export const actionMessages = {
  [ThreadAction.DELETE]: 'Tem certeza que deseja excluir?',
  [ThreadAction.BLOCK]: 'Tem certeza que deseja bloquear esta conversa?',
  [ThreadAction.ARCHIVE]: 'Tem certeza que deseja arquivar esta conversa?',
  [ThreadAction.UNIGNORE]: 'Tem certeza que deseja não ignorar esta conversa?',
  [ThreadAction.IGNORED]: 'Tem certeza que deseja ignorar esta conversa?',
};

export type AssistantAbsence = {
  scheduleMessage?: boolean;
  pauseMessage?: boolean;
  message?: string | null;
  debounceMinutes?: number;
  attendanceSchedule?: IntegrationCalendarSchedule[] | null;
};

export type Assistant = {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  absence?: AssistantAbsence | null;
  voice: {
    provider: string;
    voiceName: string;
  };
  role?: string;
  objective?: string;
  tone?: string;
  temperature?: Temperature;
  answerLength?: AnswerLength;
  typingTime?: TypingTime;
  greeting?: string;
  inferenceLevel?: InferenceLevel;
  escalateToHuman?: EscalateOrAskToHuman;
  escalateToHumanRules?: string;
  askToHuman?: EscalateOrAskToHuman;
  askToHumanRules?: string;
  preferEscalate?: boolean;
  noInformationExcuse?: string;
  prompt: string;
  promptVoice?: string;
  missionRoutingMode?: AssistantMissionRoutingMode;
  organizationId: string;
  defaultMissionId?: string | null;
  missionRoutingPrompt?: string | null;
  organization: Organization;
  createdAt: string;
  updatedAt: string;
  channels?: Channel[];
  modelName?: AssistantModel | null;
};

export enum AssistantMissionRoutingMode {
  DEFAULT_MISSION = 'DEFAULT_MISSION',
  ROUTER = 'ROUTER',
}

export enum AssistantModel {
  NULL = 'null',
  // GPT-5.4
  GPT_5_4_MINI = 'gpt_5_4_mini',
  GPT_5_4_MINI_NONE_SHORT = 'gpt_5_4_mini_none_short',
  GPT_5_4_MINI_LOW_SHORT = 'gpt_5_4_mini_low_short',
  GPT_5_4_NANO_NONE_SHORT = 'gpt_5_4_nano_none_short',
  GPT_5_4_NANO_LOW_SHORT = 'gpt_5_4_nano_low_short',
  // GPT-5.2 / 5.1
  GPT_5_2_LOW_SHORT = 'gpt_5_2_low_short',
  GPT_5_1_LOW_SHORT = 'gpt_5_1_low_short',
  GPT_5_2_NONE_SHORT = 'gpt_5_2_none_short',
  GPT_5_1_NONE_SHORT = 'gpt_5_1_none_short',
  // GPT-5 presets
  GPT_5_MINI_LOW_SHORT = 'gpt_5_mini_low_short',
  GPT_5_MINI_MINIMAL_SHORT = 'gpt_5_mini_minimal_short',
  GPT_4_1_MINI = 'gpt_4_1_mini',
  GPT_4_1 = 'gpt_4_1',
  // Anthropic
  CLAUDE_4_6_OPUS = 'claude_4_6_opus',
  CLAUDE_4_6_SONNET = 'claude_4_6_sonnet',
  CLAUDE_4_6_HAIKU = 'claude_4_6_haiku',
  // Gemini 3.1 Pro
  GEMINI_3_1_PRO_MINIMAL = 'gemini_3_1_pro_minimal',
  GEMINI_3_1_PRO_LOW = 'gemini_3_1_pro_low',
  // Gemini 3 Flash
  GEMINI_3_FLASH_MINIMAL = 'gemini_3_flash_minimal',
  GEMINI_3_FLASH_LOW = 'gemini_3_flash_low',
  // Gemini 3.1 Flash Lite
  GEMINI_3_1_FLASH_LITE_MINIMAL = 'gemini_3_1_flash_lite_minimal',
  GEMINI_3_1_FLASH_LITE_LOW = 'gemini_3_1_flash_lite_low',
  // Gemini 2.5
  GEMINI_2_5_PRO = 'gemini_2_5_pro',
  GEMINI_2_5_FLASH = 'gemini_2_5_flash',
  KIMI_K2_INSTRUCT = 'kimi_k2_instruct',
}

export const AssistantModelLabels = {
  [AssistantModel.NULL]: 'Modelo padrão do Agento',
  // GPT-5.4
  [AssistantModel.GPT_5_4_MINI]: 'GPT 5.4 mini',
  [AssistantModel.GPT_5_4_MINI_NONE_SHORT]: 'GPT 5.4 mini · none',
  [AssistantModel.GPT_5_4_MINI_LOW_SHORT]: 'GPT 5.4 mini · low',
  [AssistantModel.GPT_5_4_NANO_NONE_SHORT]: 'GPT 5.4 nano · none',
  [AssistantModel.GPT_5_4_NANO_LOW_SHORT]: 'GPT 5.4 nano · low',
  // GPT-5.2 / 5.1
  [AssistantModel.GPT_5_2_LOW_SHORT]: 'GPT 5.2 · low',
  [AssistantModel.GPT_5_1_LOW_SHORT]: 'GPT 5.1 · low',
  [AssistantModel.GPT_5_2_NONE_SHORT]: 'GPT 5.2 · none',
  [AssistantModel.GPT_5_1_NONE_SHORT]: 'GPT 5.1 · none',
  // GPT-5 presets
  [AssistantModel.GPT_5_MINI_LOW_SHORT]: 'GPT 5 mini · low',
  [AssistantModel.GPT_5_MINI_MINIMAL_SHORT]: 'GPT 5 mini · minimal',
  [AssistantModel.GPT_4_1_MINI]: 'GPT 4.1 mini',
  [AssistantModel.GPT_4_1]: 'GPT 4.1',
  // Anthropic
  [AssistantModel.CLAUDE_4_6_OPUS]: 'Claude Opus 4.6',
  [AssistantModel.CLAUDE_4_6_SONNET]: 'Claude Sonnet 4.6',
  [AssistantModel.CLAUDE_4_6_HAIKU]: 'Claude Haiku 4.6',
  // Gemini 3.1 Pro
  [AssistantModel.GEMINI_3_1_PRO_MINIMAL]: 'Gemini 3.1 Pro · minimal',
  [AssistantModel.GEMINI_3_1_PRO_LOW]: 'Gemini 3.1 Pro · low',
  // Gemini 3 Flash
  [AssistantModel.GEMINI_3_FLASH_MINIMAL]: 'Gemini 3 Flash · minimal',
  [AssistantModel.GEMINI_3_FLASH_LOW]: 'Gemini 3 Flash · low',
  // Gemini 3.1 Flash Lite
  [AssistantModel.GEMINI_3_1_FLASH_LITE_MINIMAL]: 'Gemini 3.1 Flash Lite · minimal',
  [AssistantModel.GEMINI_3_1_FLASH_LITE_LOW]: 'Gemini 3.1 Flash Lite · low',
  // Gemini 2.5
  [AssistantModel.GEMINI_2_5_PRO]: 'Gemini 2.5 Pro',
  [AssistantModel.GEMINI_2_5_FLASH]: 'Gemini 2.5 Flash',
  [AssistantModel.KIMI_K2_INSTRUCT]: 'Kimi K2',
};

export const AssistantModelOptions = Object.entries(AssistantModelLabels).map(([value, label]) => ({
  value,
  label,
}));

export enum AnswerLength {
  SHORT = 'SHORT',
  MEDIUM = 'MEDIUM',
  LONG = 'LONG',
}

export const AnswerLengthLabels = {
  [AnswerLength.SHORT]: 'Curtas',
  [AnswerLength.MEDIUM]: 'Médias',
  [AnswerLength.LONG]: 'Longas',
};

export const AnswerLengthOptions = Object.entries(AnswerLengthLabels).map(([value, label]) => ({
  value,
  label,
}));

export enum TypingTime {
  NONE = 'NONE',
  SHORT = 'SHORT',
  MEDIUM = 'MEDIUM',
  LONG = 'LONG',
}

const TypingTimeLabels = {
  [TypingTime.NONE]: 'Nenhum',
  [TypingTime.SHORT]: 'Curto',
  [TypingTime.MEDIUM]: 'Médio',
  [TypingTime.LONG]: 'Longo',
};

export const DescriptionTimeLabels: Record<TypingTime, string> = {
  [TypingTime.NONE]: 'Mais rápido possível',
  [TypingTime.SHORT]: 'Cerca de 6 segundos',
  [TypingTime.MEDIUM]: 'Cerca de 15 segundos',
  [TypingTime.LONG]: 'Cerca de 20 segundos',
};

export const TypingTimeOptions = Object.entries(TypingTimeLabels).map(([value, label]) => ({
  value,
  label,
}));

export enum EscalateOrAskToHuman {
  ALWAYS = 'ALWAYS',
  NEVER = 'NEVER',
  LOW_CONFIDENCE = 'LOW_CONFIDENCE',
}

const EscalateOrAskToHumanLabels = {
  [EscalateOrAskToHuman.ALWAYS]: 'Sempre',
  [EscalateOrAskToHuman.NEVER]: 'Nunca',
  [EscalateOrAskToHuman.LOW_CONFIDENCE]: 'Quando não souber',
};

export const EscalateOrAskToHumanOptions = Object.entries(EscalateOrAskToHumanLabels).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

export enum InferenceLevel {
  STRICT = 'STRICT',
  MODERATE = 'MODERATE',
  OPEN = 'OPEN',
}

export const InferenceLevelLabels = {
  [InferenceLevel.STRICT]: 'Estrito',
  [InferenceLevel.MODERATE]: 'Moderado',
  [InferenceLevel.OPEN]: 'Conversador',
};

export const InferenceLevelDescriptions = {
  [InferenceLevel.STRICT]: 'Fala somente o que está descrito',
  [InferenceLevel.MODERATE]: 'Segue as instruções e vai mais além',
  [InferenceLevel.OPEN]: 'Fala sobre qualquer assunto',
};

export const InferenceLevelOptions = Object.entries(InferenceLevelLabels).map(([value, label]) => ({
  value: getInferenceLevelNumber(value as InferenceLevel),
  label,
}));

export function getInferenceLevelNumber(value: InferenceLevel): number {
  switch (value) {
    case InferenceLevel.STRICT:
      return 0;
    case InferenceLevel.MODERATE:
      return 50;
    case InferenceLevel.OPEN:
      return 100;
    default:
      return 0;
  }
}

export function getInferenceLevelByNumber(value: number): InferenceLevel {
  switch (value) {
    case 0:
      return InferenceLevel.STRICT;
    case 50:
      return InferenceLevel.MODERATE;
    case 100:
      return InferenceLevel.OPEN;
    default:
      return InferenceLevel.STRICT;
  }
}

export enum Temperature {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

const temperatureLabels = {
  [Temperature.LOW]: 'Baixa',
  [Temperature.MEDIUM]: 'Média',
  [Temperature.HIGH]: 'Alta',
};

export const temperatureDescriptions = {
  [Temperature.LOW]: 'Pouca criatividade e mais atenção as instruções',
  [Temperature.MEDIUM]: 'Equilíbrio entre criatividade e conformidade',
  [Temperature.HIGH]: 'Mais criativo e inventivo, eventualmente menos coerente',
};

export const getTemperatureNumber = (value: Temperature): number => {
  switch (value) {
    case Temperature.LOW:
      return 0;
    case Temperature.MEDIUM:
      return 50;
    case Temperature.HIGH:
      return 100;
    default:
      return 0;
  }
};

export const getTemperatureByNumber = (value: number): Temperature => {
  switch (value) {
    case 0:
      return Temperature.LOW;
    case 50:
      return Temperature.MEDIUM;
    case 100:
      return Temperature.HIGH;
    default:
      return Temperature.LOW;
  }
};

export const TemperatureOptions = Object.entries(temperatureLabels).map(([key, value]) => ({
  value: getTemperatureNumber(key as Temperature),
  label: value,
}));

export type FollowUp = {
  id: string;
  title: string;
  interval?: number;
  executeAt?: string;
  tagIds: string[];
  //fail: string;
  doNotFollowUp: string;
  prompt: string;
};

export type OutboundStatus = 'EMPTY' | 'PAUSED' | 'RUNNING' | 'FINISHED';

export type Mission = {
  id: string;
  activityId: string;
  assistantId: string;
  title: string;
  routable?: boolean;
  routingCriteria?: string | null;
  remoteExecution?: RemoteExecutionConfig | null;
  prompt: string;
  promptAdditional?: string;
  promptNotify?: string;
  status: string;
  outboundThreads: OutboundThread[];
  activity: Activity;
  createdAt: string;
  updatedAt: string;
  engagementType: 'inbound' | 'outbound';
  followUp: FollowUp[];
  targetAudiences: TargetAudience[];
  promptSummary?: string | null;
  promptGroup?: string | null;
  outboundStatus?: OutboundStatus;
  outboundThreadsLastStartedAt?: string;
  baseMissionId?: string | null;
};

export type OutboundThread = {
  statusOrder: number;
  thread?: {
    id: string;
    lastMessageAt: string;
    sourceUnique: string;
    userInfo: {
      name?: string;
      phoneNumber: string;
      source: string;
    };
  };
  threadMission?: {
    createdAt: string;
    finishedAt: string | null;
    finishedStatus: ThreadFinishedStatus | null;
    finishedStatusReason: string | null;
    missionId: string;
    priority: number;
    status: ThreadStatus;
    statusReason: string | null;
    updatedAt: string | null;
  };
  outboundThread: {
    createdAt: string;
    id: string;
    info: string | null;
    source: string;
    sourceUnique: string | null;
    sourceUniqueInput: string;
    status: OutboundThreadStatus;
    statusOrder: number;
    threadId: string | null;
    updatedAt: string | null;
    userInfoName: string | null;
    extras: string | null;
  };
};

export enum OutboundThreadStatus {
  NEW = 'NEW',
  VERIFIED = 'VERIFIED',
  INVALID = 'INVALID',
  CONTACTED = 'CONTACTED',
  ERROR = 'ERROR',
}

export const OutboundThreadStatusLabels = {
  [OutboundThreadStatus.NEW]: 'Novo',
  [OutboundThreadStatus.VERIFIED]: 'Verificado',
  [OutboundThreadStatus.INVALID]: 'Inválido',
  [OutboundThreadStatus.CONTACTED]: 'Contatado',
  [OutboundThreadStatus.ERROR]: 'Erro',
};

export const OutboundThreadStatusColors = {
  [OutboundThreadStatus.NEW]: 'purple',
  [OutboundThreadStatus.VERIFIED]: 'teal',
  [OutboundThreadStatus.INVALID]: 'gray',
  [OutboundThreadStatus.CONTACTED]: 'green',
  [OutboundThreadStatus.ERROR]: 'red',
};

export enum ThreadStatus {
  NEW = 'NEW',
  JUST_STARED = 'JUST_STARED',
  ONGOING = 'ONGOING',
  FINISHED = 'FINISHED',
  IGNORED = 'IGNORED',
  ERRORED = 'ERRORED',
  TRANSFERRING = 'TRANSFERRING',
}

export const ThreadStatusLabels = {
  [ThreadStatus.NEW]: 'A inicar',
  [ThreadStatus.JUST_STARED]: 'Iniciado',
  [ThreadStatus.ONGOING]: 'Em andamento',
  [ThreadStatus.FINISHED]: 'Finalizado',
  [ThreadStatus.IGNORED]: 'Ignorado',
  [ThreadStatus.ERRORED]: 'Erro',
  [ThreadStatus.TRANSFERRING]: 'Transferindo',
};

export const ThreadStatusColors = {
  [ThreadStatus.NEW]: 'accent',
  [ThreadStatus.JUST_STARED]: 'accent',
  [ThreadStatus.ONGOING]: 'accent',
  [ThreadStatus.FINISHED]: 'green',
  [ThreadStatus.IGNORED]: 'gray',
  [ThreadStatus.ERRORED]: 'red',
  [ThreadStatus.TRANSFERRING]: 'orange',
};

export enum ThreadFinishedStatus {
  SUCCESSFULLY = 'SUCCESSFULLY',
  PARTIALLY = 'PARTIALLY',
  UNSUCCESSFULLY = 'UNSUCCESSFULLY',
}

export const ThreadFinishedStatusLabels = {
  [ThreadFinishedStatus.SUCCESSFULLY]: 'Sucesso',
  [ThreadFinishedStatus.PARTIALLY]: 'Sucesso parcial',
  [ThreadFinishedStatus.UNSUCCESSFULLY]: 'Sem sucesso',
};

export const ThreadFinishedStatusColors = {
  [ThreadFinishedStatus.SUCCESSFULLY]: 'green',
  [ThreadFinishedStatus.PARTIALLY]: 'lime',
  [ThreadFinishedStatus.UNSUCCESSFULLY]: 'yellow',
};

export type ThreadConversationType =
  | 'userAssistant' // Conversation started from the dashboard "Conversar como {user.shortName}" button
  | 'personAssistantTest' // Conversation started from the dashboard "Conversar como cliente" button
  | 'personAssistant' // Conversation started from public route "/falar/:assistantId" or WhatsApp
  | 'personUser' // Chat between public user and admin, no assistant involved
  | 'personUserTest'; // Chat between 2 admins

export type ThreadMission = {
  missionId: string;
  priority: number;
  status: ThreadStatus;
  statusReason: string | null;
  finishedStatus: ThreadFinishedStatus | null;
  finishedStatusReason: string | null;
  summary: string | null;
  createdAt: string;
  updatedAt: string | null;
  finishedAt: string | null;
};

export type Contact = {
  nome_contato_unidade: string;
  telefone_contato_unidade: string;
  email_contato_unidade: string;
  fax_contato_unidade: string;
  complemento_contato_unidade: string;
  endereco_contato_unidade: string;
  bairro_contato_unidade: string;
  cep_contato_unidade: string;
  municipio_contato_unidade: string;
  cpf_contato_unidade: string;
  tipo_responsavel_unidade: string;
  tipo_comercial_unidade: string;
};

export type Unity = {
  id_unidade: string;
  numero_unidade: string;
  nome_proprietario: string;
  email_proprietario: string;
  telefone_proprietario: string;
  celular_proprietario: string;
  cpf_proprietario: string;
  contatos: Contact[];
  status_inadimplencia_unidade: boolean;
};

export type UserInfo = {
  name: string;
  phoneNumber?: string | null;
  email?: string | null;
  source?: string;
  photoUrl?: string;
  fetchedAt?: string;
  whatsappStatus?: string;
  whatsapp?: {
    wuid: string;
    name: string;
    numberExists: boolean;
    picture?: string;
    status?:
      | {
          status: string;
          setAt: string;
        }
      | string;
    isBusiness?: boolean;
    email?: string;
    description?: string;
    website?: string;
  };
  condominium?: {
    unidade_federativa_condominio: string;
    cep_condominio: string;
    id_condominio: string;
    cnpj_condominio: string;
    nome_condominio: string;
    nome_fantasia_condominio: string;
    endereco_condominio: string;
    complemento_condominio: string;
    bairro_condominio: string;
    municipio_condominio: string;
  };

  unity?: Unity[];
};

export type ThreadTag = {
  tagId: string;
  role: string;
  reason?: string;
  createdAt: string;
  deletedAt?: string | null;
  assistantId: string | null;
  userId: string | null;
};

export type ThreadEscalateQueue = {
  userId: string;
  notifiedAt?: string | null;
  attendedAt?: string | null;
};

export type SourceTracking = {
  ['utm_source']: 'instagram' | 'google' | 'facebook' | 'FB_Ads';
  ['utm_medium']?: string;
  ['ctwa_ad']?: string;
  ['direct_link']?: string;
  ['group_member_list ']?: string;
  ['ctwa_clid']?: string;
  ['source_id']?: string;
  ['source_url']?: string;
  ['media_url']?: string;

  body?: string;
  title?: string;
  meta?: {
    ['form_id']?: string;
    ['form_name']?: string;
    ['ad_id']?: string;
    ['ad_name']?: string;
    ['adgroup_id']?: string;
    ['adset_id']?: string;
    ['campaign_id']?: string;
    ['campaign_name']?: string;
    ['event_field']?: string;
    ['leadgen_id']?: string;
    ['page_id']?: string;
    ['page_name']?: string;
    ['platform']?: string;
  };
};

export type Thread = {
  id: string;
  assistantId: string;
  missionId?: string | null; // conferir
  status?: string | null; // conferir tipo
  messages?: Message[];
  createdAt: string;
  updatedAt: string;
  summary?: Summary;
  lastMessageAt: string;
  missions: ThreadMission[];
  threadUsers: OrganizationUser[];
  source: 'webAdmin' | 'webUser' | 'whatsapp' | 'whatsappGroup';
  sourceUnique: string | null;
  conversationType: ThreadConversationType;
  createdByUserId: string | null;
  userAgent: string | null;
  userInfo?: UserInfo | null;
  groupInfo?: GroupInfo | null;
  tags: ThreadTag[];
  reduced?: ReducedThread;
  opened?: boolean;
  isReady?: boolean;
  mock?: boolean;
  thoughts?: Thought[];
  organizationId?: string;
  followUp?: ThreadFollowUp[];
  integrations?: ThreadIntegration[];
  sourceTracking?: SourceTracking;
  ignore?: boolean | null;
  escalateQueue?: ThreadEscalateQueue[];
  notes?: Note[];
  foundIn?: ThreadFoundIn[];
  groupResponseMode?: GroupResponseMode;
  assistantInstructions?: string | null;
  associatedKnowledge?: string | null;
};

type ThreadFoundIn = {
  field: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
};

export type ThreadFollowUp = {
  id: string;
  title?: string;
  missionFollowUpId: string;
  executeAt: string;
  executedAt?: string;
  followUpReason?: string;
  ignoredReason?: string;
  createdAt: string;
  updatedAt: string;
};

export type FloatingThread = {
  id: string;
  assistantId: string;
  threadId: string;
  organizationId?: string;
  conversationType?: ThreadConversationType;
  missionId?: string;
};

export type Summary = {
  intent: string | null;
  intentDetail: string | null;
  satisfaction: number | null;
  satisfactionReason: string | null;
  sentiment: string | null;
  sentimentTrend: string | null;
  tone: string | null;
  outcome: string | null;
  reengagementRequired: boolean | null;
  timeTalkingAssistant: number | null;
  timeTalkingUser: number | null;
  timeWaitingUser: number | null;
  timeTalking: number | null;
};

export type ReducedThread = {
  id: string;
  defaultAssistantId: string;
  lastMessageContent?: string;
  lastMessageAt?: string;
  lastMessageStatus?: MessageStatus;
  assistantId?: string;
  createdByUserId?: string | null;
  organizationId?: string;
  userId?: string;
  userInfoName?: string;
  userInfoFetchedAt?: string;
  userInfoPhotoUrl?: string;
  source?: string;
  sourceUnique?: string;
  conversationType?: string;
  tags: ThreadTag[];
  mock?: boolean;
  summary?: Summary;
  attendingVia?: string;
  attendedAt?: string;
  sourceTracking?: SourceTracking;
  status?: string;
  needAttention?: boolean;
  needAttentionReason?: string;
  needAttentionResolvedAt?: string;
  needAttentionAddedAt?: string;
  needAttentionAddedUserId?: string;
  needAttentionResolvedUserId?: string;
  unreadMessagesCount?: number;
  groupInfoName?: string;
  groupInfoFetchedAt?: string;
  groupInfoPhotoUrl?: string;
};

/**
 * Checks if the object is a ReducedThread
 * @param obj Object to be checked
 * @returns True if it is a ReducedThread, false otherwise
 */
export function isReducedThread(obj: Record<string, unknown>): obj is ReducedThread {
  // We check if the object has the `id` and `defaultAssistantId` keys, which are mandatory in ReducedThread
  return obj && typeof obj.id === 'string' && typeof obj.defaultAssistantId === 'string';
}

export type MessageStatus = keyof typeof MessageStatus | null;
export const MessageStatus = {
  received: 'received',
  read: 'read',
  ready: 'ready',
  errored: 'errored',
  typing: 'typing',
  sent: 'sent',
} as const;

export type MessageRole = 'user' | 'assistant' | 'system' | 'function';

export enum MessageType {
  CONVERSATION = 'conversation',
  EXTENDED_TEXT_MESSAGE = 'extendedTextMessage',
  EDITED_MESSAGE = 'editedMessage',
  TEMPLATE_MESSAGE = 'templateMessage',
  PROTOCOL_MESSAGE = 'protocolMessage',
  POLL_CREATION_MESSAGE_V3 = 'pollCreationMessageV3',
  LOCATION_MESSAGE = 'locationMessage',
  IMAGE_MESSAGE = 'imageMessage',
  DOCUMENT_MESSAGE = 'documentMessage',
  DOCUMENT_WITH_CAPTION_MESSAGE = 'documentWithCaptionMessage',
  CONTACT_MESSAGE = 'contactMessage',
  CONTACTS_ARRAY_MESSAGE = 'contactsArrayMessage',
  AUDIO_MESSAGE = 'audioMessage',
  VIDEO_MESSAGE = 'videoMessage',
  PTV_MESSAGE = 'ptvMessage',
  STICKER_MESSAGE = 'stickerMessage',
  EPHEMERAL_MESSAGE = 'ephemeralMessage',
  LIST_MESSAGE = 'listMessage',
  BUTTONS_MESSAGE = 'buttonsMessage',
  INTERACTIVE_MESSAGE = 'interactiveMessage',
  NATIVE_FLOW_MESSAGE = 'nativeFlowMessage',
  REACTION_MESSAGE = 'reactionMessage',
  PIN_IN_CHAT_MESSAGE = 'pinInChatMessage',
  ORDER_MESSAGE = 'orderMessage',
  LIST_RESPONSE_MESSAGE = 'listResponseMessage',
}

type ThreadMessageTiming = {
  processStartedAt?: string;
  processThreadStartedAt?: string;
  processThreadEndedAt?: string;
  processEndedAt?: string;
  processThreadDurationMs?: number;
  processDurationMs?: number;
};

export type Message = {
  displayContent?: string | null;
  assistantId: string | null;
  content: string;
  originalContent?: string;
  createdAt: string;
  id: string;
  role: MessageRole;
  internalRole: string | null; // Verificar tipo se é string realmente
  channelMessageId: string | null;
  replyToChannelMessageId?: string | null;
  userId?: string | null;
  status: MessageStatus | null;
  toolCallId?: string | null;
  type?: MessageType;
  timing?: ThreadMessageTiming;
  senderName?: string;
  senderSourceUnique?: string;
  sendFailCount?: number;
};

export type Thought = {
  messageId: string;
  knowledgeUsed?: KnowledgeUsed[];
  informationAnalysis?: InformationAnalysis;
  searchTerm?: string;
};

export type ResponseBasis = 'direct' | 'inference' | 'insufficient';

export type InformationAnalysis = {
  confidence: number;
  responseBasis: ResponseBasis;
  missingInformationJustification?: string;
  informationLocationJustification?: string;
  informationSource?: string;
};

export type KnowledgeUsed = {
  title: string;
  subjects: string;
  relevanceToAnswer: number;
  embeddingCount: number;
  justification: string | string[];
};

export type OrganizationUser = {
  id: string;
  threadId: string;
  createdAt: string;
  user: User;
  name?: string;
  userId: string;
  role: string; // Verificar ENUM
  reason?: string | null;
  readAt: string | null;
  deletedAt?: string | null;
  escalateReason?: string | null;
  userJustification?: string | null;
  organizationId?: string;
  attendedAt?: string | null;
  attendingVia?: string | null;
  unreadMessagesCount?: number;
  summaryAtEscalate?: string;
};

export type OutboundSpeed = 'SUPER_SHORT' | 'SHORT' | 'MEDIUM' | 'LONG' | 'VERY_LONG';

export enum MessageImprove {
  ACTIVE = 'ALWAYS',
  INACTIVE = 'INACTIVE',
  SUGGEST = 'SUGGEST',
}

export const ThreadMessageImproveLabels = {
  [MessageImprove.ACTIVE]: 'Ativo',
  [MessageImprove.INACTIVE]: 'Inativo',
  [MessageImprove.SUGGEST]: 'Ativo como sugestão',
};

export const MessageImproveOptions = Object.entries(ThreadMessageImproveLabels).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

export type OrganizationFlags = {
  // followUpJustOnDayTime?: boolean;
  allowAddChannelsOnTrial?: boolean;
  allowAddChannelsWithoutFinishConfig?: boolean;
  allowAllOutboundContact?: boolean;
  allowBetaFeatures?: boolean;
  defaultCountryStateCode?: string;
  imagePrompt?: string;
  attendanceSchedule?: IntegrationCalendarSchedule[];
  outboundSchedule?: IntegrationCalendarSchedule[];
  followUpSchedule?: IntegrationCalendarSchedule[];
  followUpEnabled?: boolean;
  outboundSpeed?: OutboundSpeed;
  nestedKnowledge?: boolean | null;
  publicKnowledge?: boolean | null;
  aboutPrompt?: string;
  showThreadIgnored?: boolean;
  escalateToQueue: boolean;
  escalateToQueueNextMinutes: number;
  knowledgeEmbeddingSearch: boolean;
  threadsViewOnlyOwn: boolean;
  threadUnreadFirst?: boolean;
  threadNeedAttentionFirst?: boolean;
  threadUnreadFromAllUsers?: boolean;
  messageImprove?: MessageImprove;
  threadUserNeedAttentionMinutes?: number;
  threadShowAnonymousPhoneNumbers?: boolean;
  threadsViewAssistants?: boolean;
  allowSendContact?: boolean;
};

export type OnboardingStatus = {
  firstTraining: boolean;
  firstAssistant: boolean;
  firstMission: boolean;
  testThreads10: boolean;
  testThreadsCount: number;
  externalTestThreads10: boolean;
  externalTestThreadsCount: number;
  firstChannel: boolean;
  finished: boolean;
};

export type Organization = {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  assistants: Assistant[];
  subscriptions: Subscription[];
  activeSubscription?: Subscription | null;
  channels: Channel[];
  threadCounts: ThreadCounts;
  testThreadCounts: ThreadCounts;
  channelConnectedAt?: string | null;
  users?: OrganizationUser[];
  slug?: string;
  affiliateId?: string | null;
  affiliate?: Affiliate;
  flags?: OrganizationFlags;
  onboardingStatus?: OnboardingStatus;
  supportNotes?: string;
  supportGroupUrl?: string;
};

export type ThreadCounts = {
  last7Days: number;
  last30Days: number;
  lastDay: number;
};

export type Subscription = {
  id: string;
  organizationId?: string;
  preSubscriptionId?: string;
  status: SubscriptionStatus;
  type: SubscriptionType;
  cycle: SubscriptionBillingCycle;
  asaasId?: string;
  trial?: boolean;
  validUntil?: string;
  createdAt: string;
  canceledAt?: string;
  updatedAt: string;
};

export enum SubscriptionStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  INACTIVE = 'INACTIVE',
  MISSING_PAYMENT_METHOD = 'MISSING_PAYMENT_METHOD',
}

export enum SubscriptionType {
  ESSENTIAL = 'ESSENTIAL',
  ADVANCED = 'ADVANCED',
  PREMIUM = 'PREMIUM',
  // Novos planos de março de 2026
  SIMPLE_2026_03 = 'SIMPLE_2026_03',
  PROFESSIONAL_2026_03 = 'PROFESSIONAL_2026_03',
  BUSINESS_2026_03 = 'BUSINESS_2026_03',
  SIMPLE_SUPPORT_2026_03 = 'SIMPLE_SUPPORT_2026_03',
  PROFESSIONAL_SUPPORT_2026_03 = 'PROFESSIONAL_SUPPORT_2026_03',
  BUSINESS_SUPPORT_2026_03 = 'BUSINESS_SUPPORT_2026_03',
}

export enum SubscriptionBillingCycle {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  SEMIANNUALLY = 'SEMIANNUALLY',
  YEARLY = 'YEARLY',
}

export enum SubscriptionBillingType {
  CREDIT_CARD = 'CREDIT_CARD',
  BOLETO = 'BOLETO',
  PIX = 'PIX',
}

export type SubscriptionSummary = {
  id: string;
  status: SubscriptionStatus;
  type: SubscriptionType;
  cycle: SubscriptionBillingCycle;
  billingType: SubscriptionBillingType;
  nextDueDate?: string;
  nextPaymentValue?: number;
  lastPaymentDate?: string;
  lastPaymentValue?: number;
  subscriberSince: string;
  validUntil: string;
  canceledAt?: string;
  previousInvoices: SubscriptionInvoice[];
  nextSubscription?: NextSubscriptionSummary;
};

export type NextSubscriptionSummary = {
  id: string;
  status: SubscriptionStatus;
  type: SubscriptionType;
  cycle: SubscriptionBillingCycle;
  billingType: SubscriptionBillingType;
  nextDueDate?: string;
  nextPaymentValue?: number;
};

export type SubscriptionInvoice = {
  id: string;
  date: string;
  description: string;
  total: number;
  invoiceUrl: string;
  status: 'PENDING' | 'CONFIRMED' | 'RECEIVED' | 'OVERDUE';
};

export type SubscriptionInvoiceSettings = {
  invoiceCreationPeriod: string;
  daysBeforeDueDate?: number;
};

export type SubscriptionBillingAddress = {
  address: string;
  addressNumber: string;
  neighborhood: string;
  city: string;
  state: string;
  postalCode: string;
  complement?: string;
};

export interface PricingDifference {
  shouldCharge: boolean;
  costDifference: number;
  newPlanValue: number;
  nextDueDate: Date;
  isUpgrade: boolean;
  isReactivation: boolean;
}

export interface BillingInfo {
  email: string;
  phone: string;
  billingAddress: SubscriptionBillingAddress;
  invoiceSettings?: SubscriptionInvoiceSettings;
}

export type AssistantKnowledge = {
  id: string;
  assistantId: string;
  knowledgeId: string;
  assistant: Assistant;
};

export type Knowledge = {
  id: string;
  title: string;
  content: string;
  subjects: string;
  extendedSubjects?: string;
  organizationId?: string;
  externalSourceUrl?: string | null;
  externalSourceUpdatedAt?: string;
  assistantKnowledge: AssistantKnowledge[];
  createdAt: string;
  tokens: number;
  // Nested fields
  order?: number;
  parentKnowledgeId?: string | null;
  children?: Knowledge[];
  public?: boolean;
};

export type Tag = {
  id: string;
  title: string;
  shortTitle: string;
  description: string;
  createdAt: string;
  organizationId: string;
  allowUser: boolean;
  allowAssistant: boolean;
  threadIds: string[];
  deletedAt?: string;
};

export type ChannelEvent = {
  at: string;
  type?: 'connection' | 'send_error';
  state?: string;
  statusReason?: number;
  cause?: string;
  action?: string;
  isPermanent?: boolean;
  errorCode?: string;
  operation?: string;
};

export type EvolutionV2Config = {
  phoneNumber: string | null;
  instanceId: string;
  instanceName: string;
  instanceApiKey: string;
  integrationType?: string;
  isBusiness?: boolean;
  status: string;
  statusReason: string;
  statusUpdatedAt: string;
  qrcode?: string;
  qrcodeCreatedAt?: string;
  qrcodeCount?: number;
  connectingCount?: number;
  connectionRequestedAt?: string;
  connectionRequestExpired?: boolean;
  flagsRejectCall?: boolean;
  flagsRejectCallMessage?: string;
  flagsAlwaysOnline?: boolean;
  flagsAllowGroup?: boolean;
  flagsSyncFullHistory?: boolean;
  disconnectionReasonCode?: string;
  disconnectionObject?: string;
  disconnectionAt?: string;
  profilePhotoUrl?: string | null;
  profileName?: string | null;
  countMessages?: number;
  countChats?: number;
};

export type ChannelConfig = {
  evolutionV2: EvolutionV2Config;
  phoneNumber: string | null;
  evolutionInstanceName: string | null;
  evolutionInstanceApiKey: string | null;
  countryCode: string | null;
  stateCode: string | null;
};

export type Channel = {
  id: string;
  assistantId: string | null;
  organizationId: string;
  type: string;
  active: boolean;
  createdAt: string;
  config: ChannelConfig;
  status: string | null;
  authUrl?: string | null;
  connect?: {
    pairingCode: string;
    code: string;
    base64: string;
    count: number;
    qrcodeCreatedAt: string;
  };

  flags?: ChannelFlags;
  events?: ChannelEvent[];
};

export type ChannelFlags = {
  doNotAnswer?: boolean;
  answerExistingContacts?: boolean;
  answerNonExistingContacts?: boolean;
  answerNewChats?: boolean;
  answerPreviousChats?: boolean;
};

export type ChannelPost = {
  type: string;
  organizationId: string;
  assistantId: string;
};

export type ChannelPut = {
  organizationId: string;
  type: string;
  config: {
    evolutionV2: {
      settingRejectCall: boolean;
      settingRejectCallMessage: string;
      settingAlwaysOnline: boolean;
    };
  };
};

export type NotificationsPost = {
  userId: string;
  title: string;
  body: string;
  link: string;
};

export type NotificationsResponse = {
  sent: number;
  failed: number;
  temporaryError: number;
  userWithoutTokens: number;
  userDeleted: number;
};

export type NotificationsOrganizationPost = {
  organizationId: string;
  title?: string;
  body?: string;
  link?: string;
};

export type TargetAudience = {
  id: string;
  title: string;
  description: string;
  questions: Question[];
};

export type Question = {
  id: string;
  question: string;
  createdAt: string;
  relevance?: boolean;
};

export type SuggestedQuestion = {
  targetAudience: string;
  targetAudienceId: string;
  questions: Question[];
};

export type SuggestedQuestionResponse = {
  suggestedQuestions: SuggestedQuestion[];
};

export type MissionAddQuestionQuestion = {
  id: string;
  targetAudienceId: string;
  question: string;
  relevance?: boolean;
  answer?: string;
};

export type MissionAddQuestionArgs = {
  missionId: string;
  questions: MissionAddQuestionQuestion[];
};

export type IntegrationType =
  | 'Superlogica'
  | 'HubSpot'
  | 'GoogleCalendar'
  | 'GoogleDrive'
  | 'RDStationMarketing'
  | 'RDStationCRM'
  | 'Sensio'
  | 'AgentTemplate'
  | 'CVCRM';

export type RemoteExecutionMode = 'off' | 'merge' | 'replace';

export type RemoteExecutionConfig = {
  enabled: boolean;
  agentId?: string | null;
  promptMode?: RemoteExecutionMode | null;
  toolsMode?: RemoteExecutionMode | null;
  knowledgeMode?: RemoteExecutionMode | null;
  allowedToolNames?: string[];
  metadata?: unknown;
};

export type IntegrationAgentTemplateConfig = {
  baseUrl: string;
  metadata?: unknown;
};

export type Integration = {
  id: string;
  organizationId: Organization['id'];
  type: IntegrationType;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  // Types that changed accordingly to the type
  authentication: { [key: string]: string };
  configGoogleCalendar?: IntegrationCalendar[];
  configAgentTemplate?: IntegrationAgentTemplateConfig | null;
};

export type ThreadIntegration = {
  integrationId: string;
  integrationType: string;
  data: {
    contactId?: string;
    unityId?: string;
    condominiumId?: string;
    idlead?: string;
  };
  createdAt: string;
  updatedAt: string;
};

export type IntegrationCalendar = {
  id: string;
  title: string;
  prompt?: string;
  calendarTitle: string;
  calendarDescription?: string;
  duration: number;
  reminders: number[];
  noticeTime: number;
  emailRequired: boolean;
  schedule: IntegrationCalendarSchedule[];
  linkType: 'GOOGLE_MEET' | 'EXTERNAL_LINK';
  meetingType: 'ONLINE' | 'IN_PERSON';
  address?: string;
  reminderChannel?: 'EMAIL' | 'WHATSAPP';
  url?: string;
};

export type IntegrationCalendarSchedule = {
  interval: ScheduleInterval;
  start: string;
  end: string;
};

export type ScheduleInterval =
  | 'ALL'
  | 'MON_FRI'
  | 'SAT_SUN'
  | 'SUN'
  | 'MON'
  | 'TUE'
  | 'WED'
  | 'THU'
  | 'FRI'
  | 'SAT';

export enum BillingType {
  CREDIT_CARD = 'CREDIT_CARD',
  PIX = 'PIX',
  BANK_SLIP = 'BOLETO',
}

export interface BankSlip {
  identificationField: string;
  nossoNumero: string;
  barCode: string;
  bankSlipUrl: string;
  expirationDate: string;
}

export interface Pix {
  encodedImage: string;
  payload: string;
  expirationDate: string;
}

export type Affiliate = {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone: string;
  commission: number;
};

export type _Activity = {
  id: string;
  name: string;
  prompt: string;
  promptTemplate: string;
  engagementType: string;
};

export enum MessageComposing {
  typing = 'typing',
  recording = 'recording',
}

export type ThreadComposingStatus = {
  threadId: string;
  status: MessageComposing;
  delay: number; // delay in seconds
  role: 'user' | 'assistant';
  userId?: string;
  timestamp: number;
};

export type UserQueue = {
  userId: string;
  order: string;
  status: string | null;
  externalUserId: number;
  externalAgencyId: number;
};

export type AttendanceQueue = {
  id: string;
  organizationId: string;
  title: string;
  areas?: string;
  users: UserQueue[];
  externalQueueId: string;
  createdAt: string;
  updatedAt: string;
};

export type Note = {
  id: string;
  content: string;
  allowAssistant: boolean;
  createdAt: string;
  updatedAt?: string;
  userId: string;
};

export type AssistantFault =
  | 'duplication'
  | 'contradiction'
  | 'poor_writting'
  | 'poor_knowledge_grouping'
  | 'poor_meta_content'
  | 'misplaced_knowledge'
  | 'misplaced_instruction'
  | 'missplaced_info'
  | 'irrelevant_info'
  | 'semantics';

export type AssistantUnitType = 'knowledge' | 'mission_instruction' | 'assistant_instruction';

export type Snippet = {
  unitType: AssistantUnitType;
  unitTitle?: string;
  quote: string;
};

export type AssistantSuggestion = {
  fault: AssistantFault;
  detailedReason: string;
  changeToBeMade: string;
  snippets: Snippet[];
  relevance: number;
};

export type PrepareFileUploadItem = {
  id: string;
  fileName: string;
  originalFileName: string;
  uploadUrl: string;
};

export type PrepareFileUploadResponse = {
  success: boolean;
  uploads: PrepareFileUploadItem[];
};

export enum StageChangeSource {
  HUMAN = 'HUMAN',
  ASSISTANT_AUTOMATIC = 'ASSISTANT_AUTOMATIC',
  ASSISTANT_SUGGESTED = 'ASSISTANT_SUGGESTED',
  SYSTEM = 'SYSTEM',
}

export enum ThreadFunnelStageApplyMode {
  NONE = 'NONE',
  SUGGEST = 'SUGGEST',
  AUTOMATIC = 'AUTOMATIC',
}

export const applyModeOptions = [
  { value: ThreadFunnelStageApplyMode.AUTOMATIC, label: 'Assistente aplica' },
  { value: ThreadFunnelStageApplyMode.SUGGEST, label: 'Assistente sugere' },
  { value: ThreadFunnelStageApplyMode.NONE, label: 'Apenas humanos' },
];

export const applyModeLabels = {
  [ThreadFunnelStageApplyMode.NONE]: 'Apenas humanos',
  [ThreadFunnelStageApplyMode.SUGGEST]: 'Assistente sugere',
  [ThreadFunnelStageApplyMode.AUTOMATIC]: 'Assistente aplica',
};

export type FunnelStage = {
  id: string;
  funnelId: string;
  name: string;
  description?: string;
  order: number;
  color?: string;
  isTerminal: boolean;
  applyMode: ThreadFunnelStageApplyMode;
  needAttention: boolean;
  attentionRequired: boolean;
  slaMinutes?: number;
  notifyOnEnter: boolean;
  allowAssistant: boolean;
  prompt?: string;
  createdAt: string;
  updatedAt: string;
};

export type Funnel = {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  showOnDashboard: boolean;
  enableKanban: boolean;
  allowAssistant: boolean;
  assistantIds: string[];
  stages: FunnelStage[];
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
};

export enum UserStatus {
  ONLINE = 'online', // Usuário ativo e usando a aplicação
  IDLE = 'idle', // Usuário está a pelo menos 1 minutos sem interagir com a aplicação.
  AWAY = 'away', // A aplicação ainda está aberta ou no background, mas o usuário está a mais de 2 minutos sem interagir.
  OFFLINE = 'offline', // Não está conectado
}

export type UsersStatus = {
  userId: string;
  userName: string;
  lastPing: string;
  browserStatus: UserStatus;
};

type BaseGroupInfo = {
  name?: string;
  id?: string;
  photoUrl?: string;

  fetchedAt?: Date;
  failedFetchedAt?: Date;
  fetchCount?: number;
};

export type Participants = {
  id: string;
  phoneNumber: string;
  admin?: string | null;
  isSelf?: boolean;
  name?: string;
  photoUrl?: string;
  profileFetchedAt?: string;
};

type WhatsAppGroupInfo = BaseGroupInfo & {
  whatsapp?: {
    name: string;
    size: number;
    creation: Date;
    description?: string;
    fetchedAt: Date;
    restrict?: boolean;
    participants?: Participants[];
  };
};

export type GroupInfo = WhatsAppGroupInfo;
